<script lang="ts">
	/**
	 * The modal titlebar, ported from Polyverse's `AbstractModal`.
	 *
	 * Three modal title treatments had grown here and none of them was the design: flowbite's own
	 * header band (a plain `text-xl` line above a full-width rule), an `h3` with a dimmed inline
	 * icon rendered *inside* the scrolling body, and a bare `widget-title`. The one this ports is
	 * the tinted **icon chip** beside a tight 16px title, with the subtitle under it and the close
	 * button squared off at the end — and **no rule beneath**: the chip and the weight of the title
	 * already separate the band from the body, where flowbite's `divide-y` draws a line the design
	 * does not have.
	 *
	 * It goes in flowbite's `header` **slot**, not at the top of the body, which is the other half
	 * of the fix: a title inside the body scrolls away with the form under it, so a long form loses
	 * the one thing saying which record is being edited.
	 *
	 * The colours are ported as **tokens**, not as hex — `primary` is emerald there and blue here,
	 * and it should stay each app's own brand rather than becoming Polyverse's. `accent` is the one
	 * that could not come across unchanged: Polyverse pairs its accent with teal text, and this
	 * accent (`#007dd1`) is a blue, so it pairs with itself.
	 *
	 * `app.postcss` does the rest — the band's padding, the missing rule, and the geometry of the
	 * close button, which is flowbite's and cannot be replaced from here without also giving up the
	 * Escape key it is wired to.
	 */

	/** The title itself. Fill the `title` slot instead when it needs markup. */
	export let title: string = '';
	/** The line under the title: a consequence to read before submitting, usually. */
	export let subtitle: string = '';
	/** Font Awesome name, without the `fa-` prefix. Omit for a title with no chip. */
	export let faIcon: string = '';
	/** What the dialog is about, which is what the chip is tinted by. */
	export let tone: 'primary' | 'accent' | 'danger' | 'amber' | 'neutral' = 'primary';

	const TONES: Record<string, string> = {
		primary: 'bg-primary-600/15 text-primary-600 dark:text-primary-400',
		accent: 'bg-accent/15 text-accent dark:text-primary-400',
		danger: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
		amber: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
		neutral: 'bg-gray-500/15 text-gray-600 dark:text-gray-300'
	};

	$: chipClass = TONES[tone] ?? TONES.primary;
</script>

<!-- `modal-header` is the hook `app.postcss` styles the band and the close button through. -->
<div class="modal-header flex min-w-0 flex-1 items-center gap-3.5">
	{#if faIcon}
		<div class="grid h-10 w-10 shrink-0 place-items-center rounded-xl {chipClass}">
			<i class="fa-solid fa-{faIcon} text-base" />
		</div>
	{/if}
	<div class="min-w-0 flex-1 pt-0.5">
		<slot name="title">
			<h3 class="m-0 text-[16px] font-bold leading-tight tracking-tight text-gray-900 dark:text-gray-50">
				{title}
			</h3>
		</slot>
		{#if subtitle}
			<p class="mt-1 text-[12.5px] leading-relaxed text-gray-500 dark:text-gray-400">{subtitle}</p>
		{/if}
		<slot />
	</div>
</div>
